import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

// Types
interface ChoreAssignment {
  member: string;
  chores: string[];
}

interface MessTestData {
  choreAssignments: ChoreAssignment[];
  noChoresDays: string[];
  productiveDays: string[];
  stepByStepPreference: boolean;
  choreFrequency: { [key: string]: string }; // chore name -> frequency
  chorePreferences: {
    preferredTime: string;
    motivationStyle: string;
    choreStyle: string;
  };
}

interface MessTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MessTestData) => void;
  householdMembers: { name: string; age: number }[];
  initialData?: Partial<MessTestData>;
}

// Styled Components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
  padding: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 70px;
    padding: ${theme.spacing.sm};
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  max-width: 700px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${theme.colors.primaryLight}20;
  position: relative;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: 16px;
    width: calc(100% - 32px);
    max-height: calc(100vh - 32px);
    border-radius: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h2`
  color: ${theme.colors.deepDark};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  svg {
    color: ${theme.colors.primary};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${theme.colors.text};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: ${theme.colors.deepDark};
  }
`;

const StepContainer = styled.div`
  padding: ${theme.spacing.lg};
  min-height: 350px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    min-height: 300px;
  }
`;

const StepTitle = styled.h3`
  color: ${theme.colors.deepDark};
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.sm};
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
    margin-bottom: ${theme.spacing.xs};
  }
`;

const StepDescription = styled.p`
  color: ${theme.colors.text};
  font-size: 0.95rem;
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
  line-height: 1.5;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    margin-bottom: ${theme.spacing.md};
    line-height: 1.4;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: ${theme.spacing.xs};
  }
`;

const OptionTag = styled.button<{ isSelected: boolean }>`
  background-color: ${props => props.isSelected ? theme.colors.primary : 'white'};
  color: ${props => props.isSelected ? 'white' : theme.colors.text};
  border: 1px solid ${props => props.isSelected ? theme.colors.primary : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 20px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  min-height: ${theme.mobile.minTouchTarget};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.isSelected ? theme.colors.primary : theme.colors.primary + '30'};
    color: ${props => props.isSelected ? 'white' : theme.colors.deepDark};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const MemberChoreSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  border-radius: 12px;
  border: 1px solid ${theme.colors.primaryLight}20;
`;

const MemberName = styled.h4`
  color: ${theme.colors.deepDark};
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const ChoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: ${theme.spacing.xs};
  }
`;

const ChoreTag = styled.button<{ isSelected: boolean }>`
  background-color: ${props => props.isSelected ? theme.colors.secondary : 'white'};
  color: ${props => props.isSelected ? 'white' : theme.colors.text};
  border: 1px solid ${props => props.isSelected ? theme.colors.secondary : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 20px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  line-height: 1.2;
  white-space: normal;
  
  &:hover {
    background-color: ${props => props.isSelected ? theme.colors.secondary : theme.colors.secondary + '30'};
    color: ${props => props.isSelected ? 'white' : theme.colors.deepDark};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.8rem;
    min-height: 36px;
    border-radius: 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg};
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    gap: ${theme.spacing.sm};
  }
`;

const NavButton = styled.button<{ variant?: 'primary'; disabled?: boolean }>`
  background-color: ${props => 
    props.variant === 'primary' ? theme.colors.primary : 'transparent'
  };
  color: ${props => 
    props.variant === 'primary' ? 'white' : theme.colors.text
  };
  border: 1px solid ${props => 
    props.variant === 'primary' ? theme.colors.primary : 'rgba(0, 0, 0, 0.2)'
  };
  border-radius: 12px;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  min-height: ${theme.mobile.minTouchTarget};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: 0.85rem;
    min-height: 44px;
    
    &:hover:not(:disabled) {
      transform: none;
    }
    
    &:active:not(:disabled) {
      transform: scale(0.95);
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
`;

const StepDot = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => 
    props.isActive ? theme.colors.primary :
    props.isCompleted ? theme.colors.primary :
    'rgba(0, 0, 0, 0.2)'
  };
  transition: all 0.2s ease;
`;

const YesNoContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
  margin-bottom: ${theme.spacing.lg};
`;

const YesNoButton = styled.button<{ isSelected: boolean; variant: 'yes' | 'no' }>`
  background-color: ${props => props.isSelected 
    ? (props.variant === 'yes' ? theme.colors.accent : theme.colors.error)
    : 'white'};
  color: ${props => props.isSelected ? 'white' : theme.colors.text};
  border: 1px solid ${props => props.isSelected 
    ? (props.variant === 'yes' ? theme.colors.accent : theme.colors.error)
    : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 12px;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  
  &:hover {
    background-color: ${props => props.isSelected 
      ? (props.variant === 'yes' ? theme.colors.accent : theme.colors.error)
      : (props.variant === 'yes' ? theme.colors.accent + '20' : theme.colors.error + '20')};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SelectContainer = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primary}20;
  }
`;

// Common chores list
const COMMON_CHORES = [
  'Dishes', 'Laundry', 'Vacuuming', 'Mopping', 'Dusting', 'Bathroom Cleaning',
  'Kitchen Cleaning', 'Trash/Recycling', 'Bed Making', 'Grocery Shopping',
  'Cooking', 'Meal Prep', 'Garden/Yard Work', 'Pet Care', 'Car Maintenance'
];

const DAYS_OF_WEEK = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

const MessTestModal: React.FC<MessTestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  householdMembers,
  initialData = {}
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [choreAssignments, setChoreAssignments] = useState<ChoreAssignment[]>([]);
  const [noChoresDays, setNoChoresDays] = useState<string[]>([]);
  const [productiveDays, setProductiveDays] = useState<string[]>([]);
  const [stepByStepPreference, setStepByStepPreference] = useState<boolean | null>(null);
  const [choreFrequency, setChoreFrequency] = useState<{ [key: string]: string }>({});
  const [chorePreferences, setChorePreferences] = useState({
    preferredTime: '',
    motivationStyle: '',
    choreStyle: ''
  });

  // Initialize data when modal opens
  useEffect(() => {
    if (isOpen && householdMembers.length > 0) {
      // Initialize chore assignments for each household member
      const initialAssignments = householdMembers.map(member => ({
        member: member.name,
        chores: initialData.choreAssignments?.find(ca => ca.member === member.name)?.chores || []
      }));
      setChoreAssignments(initialAssignments);
      
      // Set other initial data
      setNoChoresDays(initialData.noChoresDays || []);
      setProductiveDays(initialData.productiveDays || []);
      setStepByStepPreference(initialData.stepByStepPreference ?? null);
      setChoreFrequency(initialData.choreFrequency || {});
      setChorePreferences(initialData.chorePreferences || {
        preferredTime: '',
        motivationStyle: '',
        choreStyle: ''
      });
    }
  }, [isOpen, householdMembers, initialData]);

  const toggleChoreForMember = (memberName: string, chore: string) => {
    setChoreAssignments(prev => prev.map(assignment => {
      if (assignment.member === memberName) {
        const isSelected = assignment.chores.includes(chore);
        return {
          ...assignment,
          chores: isSelected 
            ? assignment.chores.filter(c => c !== chore)
            : [...assignment.chores, chore]
        };
      }
      return assignment;
    }));
  };

  const toggleDay = (day: string, type: 'noChores' | 'productive') => {
    if (type === 'noChores') {
      setNoChoresDays(prev => 
        prev.includes(day) 
          ? prev.filter(d => d !== day)
          : [...prev, day]
      );
    } else {
      setProductiveDays(prev => 
        prev.includes(day) 
          ? prev.filter(d => d !== day)
          : [...prev, day]
      );
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Chore assignments
        return choreAssignments.some(assignment => assignment.chores.length > 0);
      case 1: // No chores days
        return true; // Optional step
      case 2: // Productive days
        return productiveDays.length > 0;
      case 3: // Step by step preference
        return stepByStepPreference !== null;
      case 4: // Preferences
        return chorePreferences.preferredTime && chorePreferences.motivationStyle && chorePreferences.choreStyle;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    const data: MessTestData = {
      choreAssignments,
      noChoresDays,
      productiveDays,
      stepByStepPreference: stepByStepPreference || false,
      choreFrequency,
      chorePreferences
    };
    onSubmit(data);
    onClose();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <StepTitle>Who's in charge of what?</StepTitle>
            <StepDescription>
              Let's assign chores to each household member. Don't worry, you can always change this later!
            </StepDescription>
            {choreAssignments.map((assignment, index) => (
              <MemberChoreSection key={assignment.member}>
                <MemberName>{assignment.member}</MemberName>
                <ChoreGrid>
                  {COMMON_CHORES.map(chore => (
                    <ChoreTag
                      key={chore}
                      isSelected={assignment.chores.includes(chore)}
                      onClick={() => toggleChoreForMember(assignment.member, chore)}
                    >
                      {chore}
                    </ChoreTag>
                  ))}
                </ChoreGrid>
              </MemberChoreSection>
            ))}
          </>
        );

      case 1:
        return (
          <>
            <StepTitle>Rest days</StepTitle>
            <StepDescription>
              Which days are your "no chores" days? Everyone needs a break!
            </StepDescription>
            <OptionsGrid>
              {DAYS_OF_WEEK.map(day => (
                <OptionTag
                  key={day}
                  isSelected={noChoresDays.includes(day)}
                  onClick={() => toggleDay(day, 'noChores')}
                >
                  {day}
                </OptionTag>
              ))}
            </OptionsGrid>
          </>
        );

      case 2:
        return (
          <>
            <StepTitle>Power days</StepTitle>
            <StepDescription>
              Which days are you most productive? When do you tackle the big cleaning sessions?
            </StepDescription>
            <OptionsGrid>
              {DAYS_OF_WEEK.map(day => (
                <OptionTag
                  key={day}
                  isSelected={productiveDays.includes(day)}
                  onClick={() => toggleDay(day, 'productive')}
                >
                  {day}
                </OptionTag>
              ))}
            </OptionsGrid>
          </>
        );

      case 3:
        return (
          <>
            <StepTitle>Step-by-step guidance</StepTitle>
            <StepDescription>
              Do you prefer chores to be broken down into detailed, step-by-step instructions?
            </StepDescription>
            <YesNoContainer>
              <YesNoButton
                variant="yes"
                isSelected={stepByStepPreference === true}
                onClick={() => setStepByStepPreference(true)}
              >
                Yes, please!
              </YesNoButton>
              <YesNoButton
                variant="no"
                isSelected={stepByStepPreference === false}
                onClick={() => setStepByStepPreference(false)}
              >
                No, keep it simple
              </YesNoButton>
            </YesNoContainer>
          </>
        );

      case 4:
        return (
          <>
            <StepTitle>Your chore style</StepTitle>
            <StepDescription>
              Tell us about your cleaning preferences to help us personalize your experience.
            </StepDescription>
            
            <SelectContainer>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Preferred time for chores:
              </label>
              <Select
                value={chorePreferences.preferredTime}
                onChange={(e) => setChorePreferences(prev => ({ ...prev, preferredTime: e.target.value }))}
              >
                <option value="">Select preferred time</option>
                <option value="morning">Morning (6AM - 12PM)</option>
                <option value="afternoon">Afternoon (12PM - 6PM)</option>
                <option value="evening">Evening (6PM - 10PM)</option>
                <option value="night">Night (10PM - 6AM)</option>
                <option value="flexible">I'm flexible</option>
              </Select>
            </SelectContainer>

            <SelectContainer>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                What motivates you most?
              </label>
              <Select
                value={chorePreferences.motivationStyle}
                onChange={(e) => setChorePreferences(prev => ({ ...prev, motivationStyle: e.target.value }))}
              >
                <option value="">Select motivation style</option>
                <option value="rewards">Rewards and treats</option>
                <option value="music">Music and podcasts</option>
                <option value="efficiency">Getting it done quickly</option>
                <option value="perfection">Doing it perfectly</option>
                <option value="teamwork">Working together</option>
                <option value="routine">Having a set routine</option>
              </Select>
            </SelectContainer>

            <SelectContainer>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Your cleaning style:
              </label>
              <Select
                value={chorePreferences.choreStyle}
                onChange={(e) => setChorePreferences(prev => ({ ...prev, choreStyle: e.target.value }))}
              >
                <option value="">Select your style</option>
                <option value="little-often">Little and often</option>
                <option value="big-sessions">Big cleaning sessions</option>
                <option value="as-needed">Clean as needed</option>
                <option value="scheduled">Strict schedule</option>
                <option value="mood-based">When I'm in the mood</option>
              </Select>
            </SelectContainer>
          </>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M19.36 2.72L20.78 4.14L15.06 9.85C16.13 11.39 16.28 13.24 15.38 14.44L9.06 8.12C10.26 7.22 12.11 7.37 13.65 8.44L19.36 2.72M5.93 17.57C3.92 15.56 2.69 13.16 2.35 10.92L7.23 8.83L14.67 16.27L12.58 21.15C10.34 20.81 7.94 19.58 5.93 17.57Z"/>
            </svg>
            The Mess Test
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <StepContainer>
          {renderStep()}
        </StepContainer>

        <ButtonContainer>
          <NavButton 
            onClick={handlePrevious} 
            disabled={currentStep === 0}
          >
            Previous
          </NavButton>
          
          <StepIndicator>
            {[0, 1, 2, 3, 4].map(step => (
              <StepDot
                key={step}
                isActive={step === currentStep}
                isCompleted={step < currentStep}
              />
            ))}
          </StepIndicator>
          
          {currentStep < 4 ? (
            <NavButton 
              variant="primary" 
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </NavButton>
          ) : (
            <NavButton 
              variant="primary" 
              onClick={handleSubmit}
              disabled={!canProceed()}
            >
              Complete Test
            </NavButton>
          )}
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MessTestModal; 